// Frontend-kopi av supabase/functions/_shared/send-window.ts (CRM-1.5).
//
// Brukes til forhåndsvisning av "Sendes mandag 08:00" i SendEmailDialog og til
// toast etter sending. Logikken er IDENTISK med edge-versjonen (Deno kan ikke
// importere frontend-lib og omvendt); begge testes mot de samme vektorene i
// supabase/functions/_shared/__tests__/send-window-vectors.json. Endrer du
// logikken, endre begge. Sendevinduet: aldri før 08:00, ikke i helger eller på
// røde dager, ingen øvre klokkeslettgrense.

export interface SendWindowOptions {
  /** Første åpne time (08 = 08:00 Oslo-tid). */
  startHour: number;
  /** Stengte datoer som YYYY-MM-DD (company_holidays). */
  holidays: Set<string>;
  tz: "Europe/Oslo";
}

export interface OsloParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  /** 0 = søndag ... 6 = lørdag */
  weekday: number;
}

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const formatter = new Intl.DateTimeFormat("en-US", {
  timeZone: "Europe/Oslo",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
  weekday: "short",
});

export function osloParts(date: Date): OsloParts {
  const map: Record<string, string> = {};
  for (const p of formatter.formatToParts(date)) map[p.type] = p.value;
  return {
    year: Number(map.year),
    month: Number(map.month),
    day: Number(map.day),
    hour: Number(map.hour),
    minute: Number(map.minute),
    weekday: WEEKDAYS.indexOf(map.weekday),
  };
}

/** Oslo-lokal dato og klokkeslett til riktig UTC-øyeblikk (tåler sommer-/vintertid). */
export function osloLocalToDate(year: number, month: number, day: number, hour: number, minute = 0): Date {
  const wall = Date.UTC(year, month - 1, day, hour, minute);
  // Første gjetning: Oslo er UTC+1 eller UTC+2; rett opp med faktisk forskjell.
  let guess = wall - 2 * 3600_000;
  for (let i = 0; i < 2; i++) {
    const p = osloParts(new Date(guess));
    const shown = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute);
    guess += wall - shown;
  }
  return new Date(guess);
}

export function dateKey(p: { year: number; month: number; day: number }): string {
  return `${p.year}-${String(p.month).padStart(2, "0")}-${String(p.day).padStart(2, "0")}`;
}

export function isOpenDay(p: OsloParts, holidays: Set<string>): boolean {
  return p.weekday !== 0 && p.weekday !== 6 && !holidays.has(dateKey(p));
}

/**
 * Tidligste tillatte sendetidspunkt på eller etter `desired`.
 * - Åpen dag, kl. >= startHour: uendret.
 * - Åpen dag før startHour: samme dag kl. startHour:00.
 * - Lørdag, søndag eller rød dag: neste åpne dag kl. startHour:00.
 */
export function nextAllowedSendTime(desired: Date, opts: SendWindowOptions): Date {
  const p = osloParts(desired);
  if (isOpenDay(p, opts.holidays)) {
    if (p.hour * 60 + p.minute >= opts.startHour * 60) return desired;
    return osloLocalToDate(p.year, p.month, p.day, opts.startHour);
  }
  // Stengt dag: steg dag for dag (kalenderaritmetikk i UTC på dato-delen).
  let cursor = Date.UTC(p.year, p.month - 1, p.day);
  for (let i = 0; i < 60; i++) {
    cursor += 24 * 3600_000;
    const d = new Date(cursor);
    const y = d.getUTCFullYear();
    const m = d.getUTCMonth() + 1;
    const day = d.getUTCDate();
    const noon = osloParts(osloLocalToDate(y, m, day, 12));
    if (isOpenDay(noon, opts.holidays)) return osloLocalToDate(y, m, day, opts.startHour);
  }
  throw new Error("Fant ingen åpen sendedag de neste 60 dagene");
}

const DAY_FORMAT = new Intl.DateTimeFormat("nb-NO", {
  timeZone: "Europe/Oslo",
  weekday: "long",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
});

/** "mandag 08:00" i Oslo-tid. */
export function formatSendTime(date: Date): string {
  const parts: Record<string, string> = {};
  for (const p of DAY_FORMAT.formatToParts(date)) parts[p.type] = p.value;
  return `${parts.weekday} ${parts.hour}:${parts.minute}`;
}

/** Kort forklaring til brukeren: sendes nå, eller når meldingen går. */
export function describeSendTime(desired: Date, opts: SendWindowOptions): { sendNow: boolean; sendAt: Date; label: string } {
  const sendAt = nextAllowedSendTime(desired, opts);
  const sendNow = sendAt.getTime() === desired.getTime();
  return { sendNow, sendAt, label: sendNow ? "Sendes nå" : `Sendes ${formatSendTime(sendAt)}` };
}
